import {
  CircularProgress,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from "@mui/material";

export interface MentionUser {
  id: number;
  commonName: string;
  email?: string | null;
}

interface MentionPickerProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  users: MentionUser[];
  loading?: boolean;
  /** True when the candidate lookup failed, so a failure never reads as "no matches". */
  loadFailed?: boolean;
  highlightedIndex: number;
  onHighlight: (index: number) => void;
  onSelect: (user: MentionUser) => void;
}

/**
 * The @-mention candidate list. Keyboard navigation lives in the editor — it owns the
 * keydown events — so this renders the current highlight rather than tracking it.
 */
export function MentionPicker({
  open,
  anchorEl,
  users,
  loading,
  loadFailed,
  highlightedIndex,
  onHighlight,
  onSelect,
}: MentionPickerProps) {
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1500 }}
      data-cy="note-mention-picker"
    >
      <Paper elevation={3} sx={{ maxHeight: 240, overflowY: "auto", minWidth: 240 }}>
        {loadFailed ? (
          <Typography variant="body2" color="error" sx={{ p: 1 }}>
            Failed to load users
          </Typography>
        ) : null}
        {!loadFailed && loading && !users.length ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}
          >
            <CircularProgress size={14} />
            Loading users...
          </Typography>
        ) : null}
        {!loadFailed && !loading && !users.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            No matching users
          </Typography>
        ) : null}
        <MenuList dense disablePadding>
          {users.map((user, index) => (
            <MenuItem
              key={user.id}
              selected={index === highlightedIndex}
              onMouseEnter={() => onHighlight(index)}
              // The editor keeps focus, so the click must not blur it first.
              onMouseDown={event => event.preventDefault()}
              onClick={() => onSelect(user)}
              data-cy={`note-mention-option-${user.id}`}
            >
              <div>
                <Typography variant="body2">{user.commonName}</Typography>
                {user.email && (
                  <Typography variant="caption" color="text.secondary">
                    {user.email}
                  </Typography>
                )}
              </div>
            </MenuItem>
          ))}
        </MenuList>
      </Paper>
    </Popper>
  );
}
